import React from 'react';
import { motion } from 'framer-motion';
import { Crown, TrendingUp, TrendingDown, DollarSign, Award, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const TopClients = ({ clients = [] }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString()}`;
  };

  const calculateROI = (netWinnings, initialCapital) => {
    if (initialCapital === 0) return 0;
    return ((netWinnings / initialCapital) * 100).toFixed(1);
  };

  const getRankIcon = (index) => {
    switch (index) {
      case 0:
        return '🥇';
      case 1:
        return '🥈';
      case 2:
        return '🥉';
      default:
        return `#${index + 1}`;
    }
  };

  const getRankColor = (index) => {
    switch (index) {
      case 0:
        return 'from-yellow-400 to-yellow-600';
      case 1:
        return 'from-gray-300 to-gray-500';
      case 2:
        return 'from-amber-600 to-amber-800';
      default:
        return 'from-primary-400 to-primary-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-primary rounded-lg">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Top Clientes
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Por ganancias netas
            </p>
          </div>
        </div>

        <Link
          to="/admin/clients"
          className="btn-secondary text-sm"
        >
          Ver todos
          <ExternalLink className="w-4 h-4 ml-2" />
        </Link>
      </div>

      {clients.length === 0 ? (
        <div className="text-center py-8">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No hay clientes registrados
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {clients.map((client, index) => {
            const roi = calculateROI(client.net_winnings, client.initial_capital);
            const isProfit = Number(client.net_winnings) > 0;

            return (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                  index === 0
                    ? 'border-yellow-200 dark:border-yellow-800 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20'
                    : 'border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                <div className="absolute -top-2 -left-2">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${getRankColor(index)} flex items-center justify-center text-white text-xs font-bold shadow-lg`}>
                    {typeof getRankIcon(index) === 'string' && getRankIcon(index).startsWith('#')
                      ? getRankIcon(index)
                      : <span className="text-sm">{getRankIcon(index)}</span>
                    }
                  </div>
                </div>

                <div className="flex flex-col space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 flex-shrink-0 bg-gradient-primary rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white font-bold text-lg">
                        {client.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {client.full_name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {client.email}
                      </p>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Capital:</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(client.initial_capital)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Transacciones:</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {client.total_transactions}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Balance:</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(client.current_balance)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">ROI:</span>
                      <p className={`font-semibold ${
                        Number(roi) > 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}>
                        {roi}%
                      </p>
                    </div>
                  </div>

                  {/* Ganancias destacadas */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-dark-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Ganancias Netas:</span>
                    <div className="flex items-center space-x-2">
                      {isProfit ? (
                        <TrendingUp className="w-4 h-4 text-success-600 dark:text-success-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                      )}
                      <span className={`font-bold text-base ${
                        isProfit
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}>
                        {formatCurrency(client.net_winnings)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Rendimiento</span>
                    <span>{roi > 0 ? '+' : ''}{roi}% ROI</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        roi > 0
                          ? 'bg-gradient-success'
                          : 'bg-gray-400 dark:bg-gray-600'
                      }`}
                      style={{
                        width: `${Math.min(Math.abs(roi), 100)}%`
                      }}
                    />
                  </div>
                </div>

              </motion.div>
            );
          })}
        </div>
      )}

      {clients.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-dark-700">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-success-50 dark:bg-success-900/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-success-700 dark:text-success-300">
                {formatCurrency(clients.reduce((sum, client) => sum + (Number(client.net_winnings) || 0), 0))}
              </p>
              <p className="text-xs text-success-600 dark:text-success-400">
                Ganancias Totales
              </p>
            </div>

            <div className="text-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Award className="w-6 h-6 text-primary-600 dark:text-primary-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-primary-700 dark:text-primary-300">
                {clients.filter(client => Number(client.net_winnings) > 0).length}
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400">
                Clientes Rentables
              </p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TopClients;